<template>
  <Modal
    name="CREATE_ITEM_PRICE_MODAL"
    ref="modalCreateItemPrice"
    :title="title"
    :width="600"
    actions
  >
    <template v-slot:body>
      <Loading v-if="isItemPriceLoading" />

      <v-form
        v-else
        ref="itemPriceForm"
        v-model="valid"
        lazy-validation
      >
        <v-autocomplete
          v-if="!itemPrice"
          v-model="item"
          :label="$t('itemPrice.label.selectItem')"
          :no-data-text="$t('table.noResults')"
          :search-input.sync="search"
          :rules="rules.required"
          :loading="isLoading"
          :items="items"
          append-icon="mdi-magnify"
          class="realtySearch"
          item-value="id"
          item-text="name"
          hide-details="auto"
          return-object
          hide-no-data
          outlined
        >
          <template v-slot:item="data">
            <div class="itemList">
              <p class="body-2 ma-0">
                {{ data.item.name }}
              </p>
              <p class="caption grey--text ma-0">
                {{ $t('itemPrice.label.in') }}
                <strong>
                  {{ data.item.category.name }}
                </strong>
              </p>
            </div>
          </template>
        </v-autocomplete>

        <div v-else>
          <v-text-field
            :label="$t('itemPrice.label.selectedItem')"
            :value="itemPrice.item.name"
            hide-details="auto"
            disabled
            readonly
            outlined
          />
        </div>

        <v-spacer class="my-4" />

        <!-- price stuff -->
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.lazy="basePrice"
              v-money="catalogCurrency()"
              :label="$t('itemPrice.label.basePrice')"
              class="amount inputAmount"
              ref="basePrice"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.lazy="comboPrice"
              v-money="catalogCurrency()"
              :label="$t('itemPrice.label.comboPrice')"
              class="amount inputAmount"
              ref="comboPrice"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="12" md="4">
            <p class="mt-n2 mb-0 caption">
              {{ $t('itemPrice.label.comboEnabler') }}
            </p>
            <v-switch
              v-model="comboEnabler"
              :label="
                comboEnabler
                  ? $t('itemPrice.action.comboIsEnabled')
                  : $t('itemPrice.action.comboIsNotEnabled')
              "
              inset
            />
          </v-col>
        </v-row>
        <!-- price stuff -->
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="isItemPriceLoading"
        class="mr-5 grey--text"
        large
        text
      >
        {{ $t('itemPrice.action.cancel') }}
      </v-btn>

      <v-btn
        @click="createItemPrice"
        :loading="isItemPriceLoading"
        color="primary"
        elevation="0"
        large
        dark
      >
        {{
          !itemPrice
            ? $t('itemPrice.action.addPrice')
            : $t('itemPrice.action.editPrice')
        }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { required } from '@/util/defaultValidationRules'
import { catalogCurrency } from '@/util/catalog'
import { clearCurrency, checkCurrencyDecimals } from '@/util/helpers'

import Modal from '@/components/app/Modal'
import HAmount from '@/components/app/HAmount'
import Loading from '@/components/app/Loading'

import { VMoney } from 'v-money'

export default {
  name: 'ItemPriceCreate',

  components: {
    Modal,
    HAmount,
    Loading,
  },

  props: {
    isAnUpdate: {
      type: Boolean,
      default: false,
    },
  },

  directives: {
    money: VMoney
  },

  data() {
    return {
      item: null,
      basePrice: 0,
      comboPrice: 0,
      comboEnabler: false,
      hasError: false,
      title: this.$t('itemPrice.action.addPrice'),
      rules: { required },
      valid: false,

      catalogCurrency,

      search: null,
      _timerId: null,
      isLoading: false,

      regexCurrency: /^\d+(?:\.\d{0,2})$/
    }
  },

  watch: {
    itemPrice(itemPrice) {
      if (itemPrice) {
        this.item = itemPrice?.item
        this.comboEnabler = itemPrice?.combo_enabler
        this.basePrice = checkCurrencyDecimals(itemPrice?.base_price)
        this.comboPrice = checkCurrencyDecimals(itemPrice?.combo_price)
      }
    },

    search(term) {
      this.isLoading = true
      clearTimeout(this._timerId)

      this._timerId = setTimeout(() => {
        if (!term) {
          this.isLoading = false
          return
        }

        this.searchItems(term)
      }, 1000)
    }
  },

  computed: {
    ...mapGetters([
      'items',
      'itemPrice',
      'priceTableId',
      'isItemLoading',
      'isItemPriceLoading',
    ]),
  },

  methods: {
    ...mapMutations(['closeAllModals']),
    ...mapActions(['fetchItems', 'fetchItemPrice', 'addItemPrice']),

    resetItemPriceFields() {
      this.$nextTick(() => {
        this.item = null
        this.basePrice = 0
        this.comboPrice = 0
        this.comboEnabler = false

        if (this.$refs.itemPriceForm) {
          this.$refs.itemPriceForm.reset()
          this.$refs.itemPriceForm.resetValidation()

          const basePriceInput = this.$refs?.basePrice?.$refs?.input
          const comboPriceInput = this.$refs?.comboPrice?.$refs?.input
          basePriceInput.value = '0.00'
          comboPriceInput.value = '0.00'
        }
      })
    },

    createItemPrice() {
      !this.basePrice ? (this.hasError = true) : (this.hasError = false)

      if (this.$refs.itemPriceForm.validate() && this.basePrice) {
        const itemPrice = {
          id: this.itemPrice ? this.itemPrice.id : null,
          itemId: this.item && this.item.id ? this.item.id : null,
          priceTableId: this.priceTableId,
          comboEnabler: this.comboEnabler ? true : false,
          comboPrice: clearCurrency(this.comboPrice),
          basePrice: clearCurrency(this.basePrice),
        }

        this.addItemPrice(itemPrice)
      }
    },

    searchItems(term) {
      this.fetchItems(term).finally(() => (this.isLoading = false))
    },

  },

  mounted() {
    this.fetchItems()
  },

  beforeDestroy() {
    this.resetItemPriceFields()
  }
}
</script>

<style scoped>
.itemList {
  width: 100%;
}
</style>
